import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select } from '../ui/newSelect';
import { Actions as CONSULTANT_ACTIONS } from '../../store/actions/consultantClientsActions';
import { toggleAlertMessage } from '../../store/action';
import { friendlyConsultantClientError } from '../utils/consultantClientUtils';

const AmendConsultantClientModal = ({ open, client, onClose, onSuccess, amendConsultantClient, showAlert }) => {
  const [form, setForm] = useState({
    planType: 'client_subscription',
    stripePriceId: '',
    unitAmount: '',
    perReportFeeVal: '',
    payMode: 'monthly_cycle',
    termMonths: 12,
    autoRenew: true,
    billingFrequency: 'monthly',
  });
  const [saving, setSaving] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    if (!client?.id) return;
    setSaving(true);
    const body = {
      planType: form.planType,
      payMode: form.payMode,
      termMonths: Number(form.termMonths) || 12,
      autoRenew: !!form.autoRenew,
      billingFrequency: form.billingFrequency,
    };
    if (form.planType === 'client_subscription') {
      body.stripePriceId = form.stripePriceId || undefined;
      body.unitAmount = form.unitAmount !== '' ? Number(form.unitAmount) : undefined;
    } else {
      body.perReportFeeVal = form.perReportFeeVal !== '' ? Number(form.perReportFeeVal) : undefined;
    }

    const result = await amendConsultantClient(client.id, body);
    setSaving(false);

    if (result.ok) {
      showAlert(true, 'Amendment order form created.', 'success', 5000);
      if (result.data?.agreementUrl) window.open(result.data.agreementUrl, '_blank', 'noopener');
      onSuccess?.(result.data);
    } else {
      showAlert(true, friendlyConsultantClientError(result.error || result.message), 'error', 6000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='bg-card text-card-foreground border-border max-w-md'>
        <DialogHeader>
          <DialogTitle>Change plan — {client?.name || client?.accountName}</DialogTitle>
        </DialogHeader>

        <div className='space-y-3'>
          <div>
            <Label>Plan type</Label>
            <Select
              value={form.planType}
              onValueChange={(v) => update('planType', v)}
              options={[
                { value: 'client_subscription', label: 'Client subscription' },
                { value: 'per_report', label: 'Per report' },
              ]}
            />
          </div>
          {form.planType === 'client_subscription' && (
            <>
              <div>
                <Label>Stripe price ID</Label>
                <Input value={form.stripePriceId} onChange={(e) => update('stripePriceId', e.target.value)} />
              </div>
              <div>
                <Label>Unit amount (major units)</Label>
                <Input type='number' min='0' step='0.01' value={form.unitAmount} onChange={(e) => update('unitAmount', e.target.value)} />
              </div>
            </>
          )}
          {form.planType === 'per_report' && (
            <div>
              <Label>Per-report fee</Label>
              <Input
                type='number'
                min='0'
                step='0.01'
                value={form.perReportFeeVal}
                onChange={(e) => update('perReportFeeVal', e.target.value)}
              />
            </div>
          )}
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <Label>Pay mode</Label>
              <Select
                value={form.payMode}
                onValueChange={(v) => update('payMode', v)}
                options={[
                  { value: 'monthly_cycle', label: 'Monthly cycle' },
                  { value: 'pay_in_full', label: 'Pay in full' },
                ]}
              />
            </div>
            <div>
              <Label>Term (months)</Label>
              <Input type='number' min='1' value={form.termMonths} onChange={(e) => update('termMonths', e.target.value)} />
            </div>
          </div>
          <p className='text-xs text-muted-foreground'>
            Creates an amendment order form. The client or consultant must accept before the schedule updates.
          </p>
        </div>

        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={handleSubmit}>
            Create amendment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => ({
  amendConsultantClient: (orgId, body) => dispatch(CONSULTANT_ACTIONS.amendConsultantClient(orgId, body)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(null, mapDispatchToProps)(AmendConsultantClientModal);
